import {
  ACTION_ARGS,
  Arg,
  COMPONENT_STATE_ARGS,
  INPUT_FORM_ARGS,
  INPUT_GROUP_ARGS,
  Metadata,
  pick,
} from '../../../../utils/spds/metadata';
import { hash } from '@ember/helper';
import GroupLayout from './layout.gjs';
import RadioButton from '../radio-button.gjs';
import Super from '../../core/base.gjs';

const METADATA = Metadata.define({
  description: `Selection controls for when a user can only select one item from a list, where all of the items are shown.

### Required arguments
The \`@groupName\` and \`@onClick\` arguments are always required.
An argument called \`@radioData\` is required when not using the \`:inputs\` named block.

#### \`@groupName\`
The \`@groupName\` argument is required to properly group together the radio buttons within the group.

#### \`@onClick\`
The \`@onClick\` action is required to get the state of the radio button on user interaction.
It receives an object with attributes named \`checked\`, \`event\`, \`groupName\`, \`id\`, and \`value\`.

#### \`@radioData\`
The \`@radioData\` argument must be an array of objects that describe the radio buttons to be added to the group.
The data that can be added to a radio button is as follows:
* \`checked\`: optional to indicate default checked option
* \`label\`: required to properly label created radio button
* \`value\`: required to properly pass back a value to \`@onClick\`

When using the \`@radioData\` argument to create the group, element IDs for the included \`input\`s will be computed as follows:
* \`#\${groupName}-\${groupId}-\${inputIndex}\` for the \`input\` element
* \`#\${groupName}-\${groupId}-\${inputIndex}-label\` for the matching \`label\` element

### Named blocks
This component exposes an \`:inputs\` named block that can be used to add customized radio buttons to the group in place of the default method of passing \`@radioData\`.
The \`:inputs\` named block yields a hash of data from the \`Spds::Input::Group::RadioButton\` component that includes: \`disabled\`, \`groupName\`, \`id\`, \`onBlur\`, \`onChange\`, \`onClick\`, \`onFocus\`, \`required\`.
When using the \`:inputs\` named block to create an input group, the \`@name\` value must be passed to the inputs to properly include them in the group.

### Contextual components
The radio button is also passed into the group using the \`radio\` key.
Values for \`checked\`, \`label\`, and \`value\` can be passed directly into the yielded radio button.

### Event handling
The \`@onBlur\`, \`@onChange\`, and \`@onFocus\` actions are optional and are forwarded uniformly to every radio button rendered within the group, regardless of whether \`@radioData\`, contextual components, or the \`:inputs\` named block is used.
Each action receives an object with attributes named \`checked\`, \`event\`, \`groupName\`, \`id\`, and \`value\`, matching the format of \`@onClick\`.`,
  extends: [
    pick(ACTION_ARGS, ['onBlur', 'onChange', 'onFocus', { name: 'onClick', required: true }]),
    pick(COMPONENT_STATE_ARGS, ['disabled']),
    pick(INPUT_GROUP_ARGS, [
      { name: 'description', initialValue: 'Radio group description' },
      { name: 'groupName', initialValue: 'styleguide-radio-group' },
      { name: 'hideDescription', defaultValue: false },
      { name: 'layout', defaultValue: 'column' },
    ]),
    pick(INPUT_FORM_ARGS, ['required']),
  ],
  meta: {
    radioData: Arg.array({ description: 'Array of { label, value, checked } for each option' }),
  },
  name: 'Group Radio Button',
  resources: {
    figma:
      'https://www.figma.com/design/g4zf0Vkj39VcCyleokv2rl/%E2%9D%96-Air-web-components?node-id=24908-9851&m=dev',
    zeroheight: 'https://www.simplepractice.design/1d0abbc0a/p/613f82-radio-button',
  },
});

export default class SpdsInputGroupRadioButton extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);

  baseClass = 'spds-input-radio-button-group';
  type = 'radio-button';

  get hideDescription() {
    return this.argOrDefault('hideDescription');
  }

  get layout() {
    return this.argOrDefault('layout');
  }

  <template>
    <GroupLayout
      @component={{this.componentName}}
      @groupName={{@groupName}}
      @hideDescription={{this.hideDescription}}
      @id={{this.id}}
      @layout={{this.layout}}
      @onBlur={{@onBlur}}
      @onChange={{@onChange}}
      @onClick={{@onClick}}
      @onFocus={{@onFocus}}
      @required={{@required}}
      class={{this.classes}}
      data-test-spds-input-group-radio-button
      {{this.validateArguments}}
      ...attributes
    >
      <:description>
        {{#if (has-block "description")}}
          {{yield to="description"}}
        {{else}}
          {{@description}}
        {{/if}}
      </:description>
      <:inputs as |group|>
        {{#if (has-block "inputs")}}
          {{yield
            (hash
              disabled=@disabled
              groupName=group.groupName
              id=group.id
              onBlur=group.onBlur
              onChange=group.onChange
              onClick=group.onClick
              onFocus=group.onFocus
              required=@required
            )
            to="inputs"
          }}
        {{else if @radioData}}
          {{#each @radioData as |radio index|}}
            <RadioButton
              @checked={{radio.checked}}
              @disabled={{@disabled}}
              @id="{{group.id}}-{{index}}"
              @label={{radio.label}}
              @groupName={{group.groupName}}
              @onBlur={{group.onBlur}}
              @onChange={{group.onChange}}
              @onClick={{group.onClick}}
              @onFocus={{group.onFocus}}
              @required={{@required}}
              @value={{radio.value}}
            />
          {{/each}}
        {{else}}
          {{yield
            (hash
              radio=(component
                RadioButton
                disabled=@disabled
                groupName=group.groupName
                onBlur=group.onBlur
                onChange=group.onChange
                onClick=group.onClick
                onFocus=group.onFocus
                required=@required
              )
            )
          }}
        {{/if}}
      </:inputs>
    </GroupLayout>
  </template>
}

export const INPUT_GROUP_RADIO_BUTTON_METADATA = SpdsInputGroupRadioButton.metadata;
